// views/appcopy/screens.ts — the rail: which screen each string belongs to, and
// the layout that draws it on the phone.
//
// HOW THIS MAPS TO THE DATA. The rows in D1 carry a `screen` ("Today") and a
// `where` ("Bottom card — button"). That's enough to group and label, but not to
// draw. So each rail entry claims a screen plus a set of key prefixes, and lists
// its STATES — because most of this app's copy only appears after something
// happens (a modal opens, the binder window starts, the day is logged). The rule
// from the design holds: if copy is only visible after an interaction, that
// interaction becomes a selectable state — you never click around inside the
// preview to reach a string.
//
// Nothing here gates editing. A key that no layout mentions still gets its field
// in the panel (it lands in that screen's "Other copy" entry), and a layout that
// names a key the data doesn't have simply renders nothing. Adding a screen later
// = add its blocks; the rail entry and counts follow the data automatically.

export type Chrome = "today" | "sheet" | "modal" | "page" | "plain" | "notification";

export type Block =
  | { t: "eyebrow"; k: string }
  | { t: "title"; k: string; small?: boolean }
  | { t: "lead"; k: string }
  | { t: "note"; k: string }
  | { t: "small"; k: string; center?: boolean }
  | { t: "section"; k: string }
  | { t: "bullets"; ks: string[] }
  | { t: "field"; k: string }
  | { t: "btn"; k: string; tone?: "yellow" | "dark" | "ghost" | "red" | "purple" }
  | { t: "link"; k: string }
  | { t: "error"; k: string }
  | { t: "chips"; ks: string[] }
  | { t: "options"; items: { title: string; sub?: string }[] }
  | { t: "rows"; items: { label: string; sub?: string; value?: string }[] }
  | { t: "checks"; items: { label: string; value?: string; done?: boolean }[] }
  | { t: "video"; k: string; meta?: string }
  | { t: "pill"; k: string }
  | { t: "banner"; k: string; sub?: string }
  | { t: "steps"; items: { title: string; sub?: string }[] }
  | { t: "stats"; ks: string[] }
  | { t: "oneof"; ks: string[]; note?: string }   // alternatives sharing one slot
  // Heard, not seen — a screen-reader label for a control whose meaning is a picture.
  | { t: "a11y"; k?: string; ks?: string[] }
  | { t: "gap"; h?: number };

export type StateDef = {
  id: string;
  label: string;
  chrome: Chrome;
  blocks: Block[];
  tabHighlight?: boolean;
  caption?: string; // one line under the phone
};

export type ScreenDef = {
  id: string;
  label: string;
  flow: string;
  screen: string;        // must match ContentRow.screen
  prefixes?: string[];   // keys this entry owns; omitted = every key on that screen
  states: StateDef[];    // empty = no preview yet (still editable)
  note?: string;
};

export const FLOW_ORDER = ["Sign in", "Onboarding", "Tracker", "Progress", "Support", "Settings", "Messaging", "Everywhere"];

export const SCREENS: ScreenDef[] = [
  // ------------------------------------------------------------ Sign in
  {
    id: "login", label: "Sign in", flow: "Sign in", screen: "Sign in",
    states: [{
      id: "default", label: "Default", chrome: "plain",
      caption: "The first screen when nobody is signed in.",
      blocks: [
        { t: "gap", h: 40 },
        { t: "title", k: "login.title" },
        { t: "lead", k: "login.lead" },
        { t: "gap", h: 6 },
        { t: "field", k: "login.placeholder" },
        { t: "btn", k: "login.cta" },
        { t: "small", k: "login.footnote", center: true },
      ],
    }],
  },
  {
    id: "verify", label: "Enter code", flow: "Sign in", screen: "Enter code",
    states: [{
      id: "default", label: "Waiting for the code", chrome: "plain",
      blocks: [
        { t: "gap", h: 40 },
        { t: "title", k: "verify.title" },
        { t: "lead", k: "verify.lead" },
        { t: "gap", h: 6 },
        { t: "field", k: "verify.placeholder" },
        { t: "btn", k: "verify.cta" },
        { t: "link", k: "verify.change" },
        { t: "gap", h: 6 },
        { t: "error", k: "verify.error" },
      ],
    }],
  },
  {
    id: "nomatch", label: "No order found", flow: "Sign in", screen: "No order found",
    states: [{
      id: "default", label: "Email matches no order", chrome: "plain",
      blocks: [
        { t: "gap", h: 40 },
        { t: "title", k: "nomatch.title" },
        { t: "lead", k: "nomatch.lead" },
        { t: "gap", h: 6 },
        { t: "btn", k: "nomatch.cta" },
        { t: "link", k: "nomatch.support" },
      ],
    }],
  },

  // ------------------------------------------------------------ Onboarding
  {
    id: "walkthrough", label: "Getting Started", flow: "Onboarding", screen: "Getting Started",
    note: "Eight cards, shown once after the first sign-in.",
    states: [
      {
        id: "c1", label: "1 · Welcome", chrome: "plain",
        blocks: [
          { t: "gap", h: 30 },
          { t: "eyebrow", k: "start.welcome.eyebrow" },
          { t: "title", k: "start.welcome.title" },
          { t: "lead", k: "start.welcome.lead" },
          { t: "gap", h: 10 },
          { t: "btn", k: "start.btn.continue" },
          { t: "link", k: "start.btn.skip" },
        ],
      },
      {
        id: "c2", label: "2 · In order", chrome: "plain",
        blocks: [
          { t: "gap", h: 30 },
          { t: "eyebrow", k: "start.order.eyebrow" },
          { t: "title", k: "start.order.title" },
          { t: "lead", k: "start.order.lead" },
          { t: "gap", h: 10 },
          { t: "btn", k: "start.btn.continue" },
        ],
      },
      {
        id: "c3", label: "3 · Timing", chrome: "plain",
        blocks: [
          { t: "gap", h: 30 },
          { t: "eyebrow", k: "start.timing.eyebrow" },
          { t: "title", k: "start.timing.title" },
          { t: "lead", k: "start.timing.lead" },
          { t: "bullets", ks: ["start.timing.bullet1", "start.timing.bullet2", "start.timing.bullet3"] },
          { t: "gap", h: 6 },
          { t: "btn", k: "start.btn.continue" },
        ],
      },
      {
        id: "c4", label: "4 · Breakfast", chrome: "plain",
        caption: "They have to pick one before Continue turns on.",
        blocks: [
          { t: "gap", h: 24 },
          { t: "eyebrow", k: "start.fasting.eyebrow" },
          { t: "title", k: "start.fasting.title" },
          { t: "lead", k: "start.fasting.lead" },
          { t: "options", items: [
            { title: "start.fasting.opt.eats.title", sub: "start.fasting.opt.eats.sub" },
            { title: "start.fasting.opt.supps.title", sub: "start.fasting.opt.supps.sub" },
            { title: "start.fasting.opt.fasts.title", sub: "start.fasting.opt.fasts.sub" },
          ] },
          { t: "btn", k: "start.btn.gated", tone: "ghost" },
        ],
      },
      {
        id: "c5", label: "5 · Diet", chrome: "plain",
        blocks: [
          { t: "gap", h: 24 },
          { t: "eyebrow", k: "start.diet.eyebrow" },
          { t: "title", k: "start.diet.title" },
          { t: "lead", k: "start.diet.lead" },
          { t: "section", k: "start.diet.good.heading" },
          { t: "lead", k: "start.diet.good" },
          { t: "section", k: "start.diet.bad.heading" },
          { t: "lead", k: "start.diet.bad" },
          { t: "btn", k: "start.btn.continue" },
        ],
      },
      {
        id: "c6", label: "6 · Water", chrome: "plain",
        blocks: [
          { t: "gap", h: 30 },
          { t: "eyebrow", k: "start.water.eyebrow" },
          { t: "title", k: "start.water.title" },
          { t: "lead", k: "start.water.lead" },
          { t: "bullets", ks: ["start.water.bullet1", "start.water.bullet2", "start.water.bullet3"] },
          { t: "btn", k: "start.btn.continue" },
        ],
      },
      {
        id: "c7", label: "7 · How you feel", chrome: "plain",
        blocks: [
          { t: "gap", h: 28 },
          { t: "eyebrow", k: "start.symptoms.eyebrow" },
          { t: "title", k: "start.symptoms.title" },
          { t: "lead", k: "start.symptoms.lead" },
          { t: "note", k: "start.symptoms.note" },
          { t: "btn", k: "start.btn.continue" },
        ],
      },
      {
        id: "c8", label: "8 · Reminders", chrome: "plain",
        blocks: [
          { t: "gap", h: 30 },
          { t: "eyebrow", k: "start.reminders.eyebrow" },
          { t: "title", k: "start.reminders.title" },
          { t: "lead", k: "start.reminders.lead" },
          { t: "gap", h: 10 },
          { t: "btn", k: "start.btn.last" },
        ],
      },
    ],
  },

  // ------------------------------------------------------------ Tracker
  // Rebuilt 2026-08-23 against the app's current card (the tracker's Aug 20-21 work
  // replaced the old dock states), reading mobile/src/app/(app)/index.tsx for the order
  // the customer actually meets these in.
  {
    id: "today", label: "Today · the card", flow: "Tracker", screen: "Today",
    prefixes: ["today.", "dock.", "card.", "sheet.done"],
    states: [
      {
        id: "notstarted", label: "Not started", chrome: "today",
        caption: "Before they've begun — the button changes with the time of day.",
        blocks: [
          { t: "oneof", ks: ["dock.cta.now", "dock.cta.tomorrow"], note: "before or after 2 hours past their reminder time" },
          { t: "video", k: "dock.howto" },
        ],
      },
      {
        id: "running", label: "Day running", chrome: "today",
        blocks: [
          { t: "lead", k: "today.dock.dayline" },
          { t: "pill", k: "card.chip.binderday" },
          { t: "oneof", ks: ["card.rest.nobinder", "card.rest.binderday"], note: "binder day or not" },
          { t: "btn", k: "today.cta.track" },
          { t: "video", k: "dock.howto" },
          { t: "small", k: "dock.howto.cancel", center: true },
        ],
      },
      {
        id: "cardopen", label: "Logging a dose", chrome: "today",
        caption: "The card opens into the three steps before it logs.",
        blocks: [
          { t: "steps", items: [
            { title: "card.step1.title", sub: "card.step1.tip" },
            { title: "card.step2.title", sub: "card.step2.tip" },
            { title: "card.step3.title", sub: "card.step3.tip" },
          ] },
          { t: "note", k: "card.open.binderNote" },
          { t: "btn", k: "card.open.confirm" },
          { t: "oneof", ks: ["card.open.foot", "card.open.foot.binder"], note: "binder day or not" },
          { t: "small", k: "card.close.a11y" },
        ],
      },
      {
        id: "justlogged", label: "The moment after logging", chrome: "today",
        blocks: [
          { t: "title", k: "card.success.binder.title", small: true },
          { t: "oneof", ks: ["card.success.binder.body", "card.success.body"], note: "binder day or not" },
          { t: "btn", k: "card.undo", tone: "ghost" },
          { t: "small", k: "card.undone" },
        ],
      },
      {
        id: "banked", label: "Day banked", chrome: "today",
        blocks: [
          { t: "title", k: "sheet.done.title", small: true },
          { t: "small", k: "card.locked.body" },
          { t: "title", k: "card.dayTitle", small: true },
          { t: "checks", items: [
            { label: "today.logged.detox", value: "card.logged.sub", done: true },
          ] },
          { t: "small", k: "card.completed" },
          { t: "btn", k: "card.progress.cta", tone: "dark" },
        ],
      },
      {
        id: "otherstage", label: "Viewing another detox", chrome: "today",
        caption: "The card when they're looking at a stage that isn't today's.",
        blocks: [
          { t: "title", k: "card.dayTitle", small: true },
          { t: "oneof", ks: ["card.stage.locked", "card.stage.done"], note: "not started yet, or already finished" },
          { t: "btn", k: "card.progress.cta", tone: "dark" },
        ],
      },
      {
        id: "paused", label: "Paused / scheduled", chrome: "today",
        blocks: [
          { t: "note", k: "dock.paused" },
          { t: "btn", k: "dock.paused.cta" },
          { t: "note", k: "dock.scheduled" },
          { t: "link", k: "dock.scheduled.cta" },
        ],
      },
    ],
  },
  {
    id: "binder", label: "The Binder block", flow: "Tracker", screen: "Today",
    prefixes: ["binder."],
    note: "Only on a binder day — every other day from day 2, and never on Heavy Metal.",
    states: [
      {
        id: "counting", label: "Window counting down", chrome: "sheet",
        blocks: [
          { t: "section", k: "binder.step.label" },
          { t: "title", k: "binder.title", small: true },
          { t: "checks", items: [{ label: "binder.window.closed" }] },
          { t: "lead", k: "binder.window.closed.body" },
          { t: "btn", k: "binder.log", tone: "ghost" },
          { t: "link", k: "binder.early.link" },
        ],
      },
      {
        id: "open", label: "Window open", chrome: "sheet",
        blocks: [
          { t: "section", k: "binder.step.label" },
          { t: "title", k: "binder.title", small: true },
          { t: "title", k: "binder.window.ready", small: true },
          { t: "checks", items: [{ label: "binder.window.open", done: true }] },
          { t: "lead", k: "binder.window.open.body" },
          { t: "btn", k: "binder.log", tone: "purple" },
        ],
      },
      {
        id: "eating", label: "Waiting on breakfast", chrome: "sheet",
        caption: "For people who said they eat breakfast — the window starts when they're done.",
        blocks: [
          { t: "title", k: "binder.eating.title", small: true },
          { t: "lead", k: "binder.eating.body" },
          { t: "btn", k: "binder.eating.cta", tone: "dark" },
        ],
      },
      {
        id: "early", label: "Took it early", chrome: "sheet",
        blocks: [
          { t: "title", k: "binder.early.title", small: true },
          { t: "lead", k: "binder.early.body" },
          { t: "btn", k: "binder.early.confirm", tone: "purple" },
          { t: "btn", k: "binder.early.cancel", tone: "ghost" },
        ],
      },
    ],
  },
  {
    id: "sheet", label: "Stage & switcher", flow: "Tracker", screen: "Today",
    prefixes: ["sheet.", "stagesheet."],
    states: [
      {
        id: "today", label: "Viewing today", chrome: "sheet",
        blocks: [{ t: "pill", k: "sheet.pill.today" }],
      },
      {
        id: "stages", label: "Finished / upcoming stage", chrome: "sheet",
        blocks: [
          { t: "lead", k: "sheet.stage.complete" },
          { t: "gap", h: 8 },
          { t: "lead", k: "sheet.stage.locked" },
        ],
      },
      {
        id: "switcher", label: "Detox switcher", chrome: "sheet",
        blocks: [{ t: "title", k: "stagesheet.title" }],
      },
    ],
  },
  {
    id: "startmodal", label: "Start modal", flow: "Tracker", screen: "Today",
    prefixes: ["start."],
    states: [
      {
        id: "now", label: "Before 9 AM", chrome: "modal",
        blocks: [
          { t: "oneof", ks: ["start.eyebrow", "start.eyebrow.solo"], note: "whether they own more than one detox" },
          { t: "title", k: "start.ready.title.now" },
          { t: "lead", k: "start.ready.body.now" },
          { t: "gap", h: 4 },
          { t: "btn", k: "start.ready.cta.now" },
          { t: "link", k: "start.ready.secondary.now" },
          { t: "small", k: "start.ready.tertiary", center: true },
        ],
      },
      {
        id: "tomorrow", label: "9 AM or later", chrome: "modal",
        blocks: [
          { t: "oneof", ks: ["start.eyebrow", "start.eyebrow.solo"], note: "whether they own more than one detox" },
          { t: "title", k: "start.ready.title.tomorrow" },
          { t: "lead", k: "start.ready.body.tomorrow" },
          { t: "gap", h: 4 },
          { t: "btn", k: "start.ready.cta.tomorrow" },
          { t: "link", k: "start.ready.secondary.tomorrow" },
          { t: "small", k: "start.ready.tertiary", center: true },
        ],
      },
    ],
  },
  {
    id: "complete", label: "Detox complete", flow: "Tracker", screen: "Today",
    prefixes: ["complete."],
    states: [{
      id: "default", label: "The moment they finish", chrome: "modal",
      blocks: [
        { t: "banner", k: "complete.banner.title", sub: "complete.banner.sub" },
        { t: "eyebrow", k: "complete.eyebrow" },
        { t: "lead", k: "complete.body" },
        { t: "gap", h: 4 },
        { t: "btn", k: "complete.cta" },
        { t: "link", k: "complete.secondary" },
      ],
    }],
  },
  {
    id: "break", label: "Break & reminder", flow: "Tracker", screen: "Today",
    prefixes: ["break.", "confirm."],
    states: [
      {
        id: "break", label: "Taking a break", chrome: "modal",
        blocks: [
          { t: "title", k: "break.title" },
          { t: "lead", k: "break.body" },
          { t: "section", k: "break.label" },
          { t: "chips", ks: ["break.chip.tomorrow", "break.chip.2days", "break.chip.3days", "break.chip.week", "break.chip.pick"] },
          { t: "btn", k: "break.cta" },
          { t: "small", k: "break.footnote", center: true },
        ],
      },
      {
        id: "confirm", label: "Reminder set", chrome: "modal",
        blocks: [
          { t: "oneof", ks: ["confirm.title.tomorrow", "confirm.title.date"], note: "tomorrow, or a specific date" },
          { t: "lead", k: "confirm.body" },
          { t: "btn", k: "confirm.cta" },
        ],
      },
    ],
  },
  {
    id: "neworder", label: "New order spotted", flow: "Tracker", screen: "Today",
    prefixes: ["order."],
    states: [
      {
        id: "kit", label: "A whole kit", chrome: "modal",
        blocks: [
          { t: "eyebrow", k: "order.eyebrow" },
          { t: "title", k: "order.title" },
          { t: "lead", k: "order.body.kit" },
          { t: "btn", k: "order.cta.kit" },
          { t: "link", k: "order.hold" },
          { t: "small", k: "order.footnote", center: true },
        ],
      },
      {
        id: "single", label: "A single jar", chrome: "modal",
        blocks: [
          { t: "eyebrow", k: "order.eyebrow.multi" },
          { t: "title", k: "order.title" },
          { t: "lead", k: "order.body.new" },
          { t: "btn", k: "order.cta.add" },
          { t: "link", k: "order.hold" },
        ],
      },
      {
        id: "queued", label: "Before / after the current one", chrome: "modal",
        blocks: [
          { t: "eyebrow", k: "order.eyebrow" },
          { t: "title", k: "order.title" },
          { t: "lead", k: "order.body.after" },
          { t: "lead", k: "order.body.before" },
          { t: "btn", k: "order.cta.after" },
          { t: "link", k: "order.hold" },
        ],
      },
    ],
  },

  // ------------------------------------------------------------ Progress
  {
    id: "progress", label: "Progress", flow: "Progress", screen: "Progress",
    prefixes: ["progress."],
    states: [
      {
        id: "started", label: "Once started", chrome: "page",
        blocks: [
          { t: "small", k: "progress.back" },
          { t: "title", k: "progress.title" },
          { t: "lead", k: "progress.hint" },
          { t: "stats", ks: ["progress.stat.ontrack", "progress.stat.streak"] },
        ],
      },
      {
        id: "notstarted", label: "Not started yet", chrome: "page",
        blocks: [
          { t: "title", k: "progress.title" },
          { t: "pill", k: "progress.notstarted.title" },
          { t: "lead", k: "progress.notstarted.body" },
          { t: "note", k: "progress.summary.notstarted" },
        ],
      },
    ],
  },
  {
    id: "daysheet", label: "Day sheet", flow: "Progress", screen: "Progress",
    prefixes: ["day."],
    note: "Opens when they tap a day on the calendar.",
    states: [
      {
        id: "done", label: "All done", chrome: "modal",
        blocks: [
          { t: "title", k: "day.taken.title" },
          { t: "rows", items: [{ label: "day.row.binder", value: "day.row.binder.pending" }] },
          { t: "small", k: "day.nobinder" },
          { t: "btn", k: "day.btn.close", tone: "ghost" },
        ],
      },
      {
        id: "pending", label: "Binder to come", chrome: "modal",
        blocks: [
          { t: "title", k: "day.binderPending.title" },
          { t: "lead", k: "day.binderPending.body" },
          { t: "rows", items: [{ label: "day.row.binder", value: "day.row.binder.pending" }] },
          { t: "btn", k: "day.btn.close", tone: "ghost" },
        ],
      },
      {
        id: "bindermissed", label: "Binder missed", chrome: "modal",
        blocks: [
          { t: "title", k: "day.binderMissed.title" },
          { t: "lead", k: "day.binderMissed.body" },
          { t: "rows", items: [{ label: "day.row.binder", value: "day.row.binder.missed" }] },
          { t: "btn", k: "day.btn.trackedBinder" },
          { t: "btn", k: "day.btn.missedBinder", tone: "ghost" },
          { t: "small", k: "day.note.trackedBinder" },
          { t: "small", k: "day.note.missedBinder" },
        ],
      },
      {
        id: "asks", label: "Two things to sort out", chrome: "modal",
        caption: "A binder day where neither dose was answered.",
        blocks: [
          { t: "title", k: "day.ask.both.title" },
          { t: "lead", k: "day.ask.both.body" },
          { t: "section", k: "day.ask.detox" },
          { t: "btn", k: "day.btn.trackedDetox" },
          { t: "btn", k: "day.btn.missedDetox", tone: "ghost" },
          { t: "section", k: "day.ask.binder" },
          { t: "small", k: "day.ask.waiting" },
          { t: "btn", k: "day.btn.trackedBinder" },
          { t: "btn", k: "day.btn.missedBinder", tone: "ghost" },
        ],
      },
      {
        id: "acked", label: "Marked as missed", chrome: "modal",
        blocks: [
          { t: "title", k: "day.missedack.title" },
          { t: "lead", k: "day.missedack.body" },
          { t: "title", k: "day.binderMissedAck.title", small: true },
          { t: "rows", items: [{ label: "day.row.binder", value: "day.row.missed" }] },
          { t: "btn", k: "day.btn.close", tone: "ghost" },
        ],
      },
      {
        id: "missed", label: "Nothing logged", chrome: "modal",
        blocks: [
          { t: "title", k: "day.missed.title" },
          { t: "lead", k: "day.missed.body" },
          { t: "btn", k: "day.btn.trackedDetox" },
          { t: "btn", k: "day.btn.missedDetox", tone: "ghost" },
          { t: "small", k: "day.note.trackedDetox" },
          { t: "small", k: "day.note.missedDetox" },
        ],
      },
      {
        id: "today", label: "Today, not logged", chrome: "modal",
        blocks: [
          { t: "title", k: "day.today.title" },
          { t: "lead", k: "day.today.body" },
          { t: "btn", k: "day.btn.goToday" },
        ],
      },
      {
        id: "future", label: "A future day", chrome: "modal",
        blocks: [
          { t: "title", k: "day.future.title" },
          { t: "oneof", ks: ["day.future.binder", "day.future.normal"], note: "binder day or not" },
          { t: "btn", k: "day.btn.close", tone: "ghost" },
        ],
      },
    ],
  },
  {
    id: "detoxes", label: "My Detoxes", flow: "Progress", screen: "My Detoxes",
    states: [{
      id: "default", label: "Default", chrome: "page",
      blocks: [
        { t: "title", k: "orders.title" },
        { t: "section", k: "orders.section.current" },
        { t: "rows", items: [
          { label: "orders.program.kit", sub: "orders.status.active" },
          { label: "orders.binder", sub: "orders.binder.sub" },
        ] },
        { t: "section", k: "orders.section.completed" },
        { t: "rows", items: [
          { label: "orders.program.partial", sub: "orders.completed.sub", value: "orders.status.complete" },
        ] },
      ],
    }],
  },

  // ------------------------------------------------------------ Support
  {
    id: "help", label: "Help", flow: "Support", screen: "Help",
    prefixes: ["help."],
    states: [
      {
        id: "ask", label: "Ask a question", chrome: "page",
        blocks: [
          { t: "title", k: "help.title" },
          { t: "lead", k: "help.sub" },
          { t: "field", k: "help.placeholder" },
          { t: "small", k: "help.thinking" },
          { t: "pill", k: "help.source" },
          { t: "link", k: "help.more" },
          { t: "link", k: "help.less" },
          { t: "gap", h: 4 },
          { t: "lead", k: "help.needPerson" },
          { t: "btn", k: "help.messageTeam", tone: "dark" },
        ],
      },
      {
        id: "form", label: "Message the team", chrome: "page",
        blocks: [
          { t: "lead", k: "help.form.lead" },
          { t: "rows", items: [
            { label: "help.form.name" },
            { label: "help.form.email" },
            { label: "help.form.question" },
          ] },
          { t: "small", k: "help.form.footnote" },
        ],
      },
      {
        id: "sent", label: "After sending", chrome: "page",
        blocks: [
          { t: "title", k: "help.sent.title" },
          { t: "lead", k: "help.sent.body" },
          { t: "btn", k: "help.sent.cta" },
          { t: "link", k: "help.sent.back" },
        ],
      },
      {
        id: "messages", label: "My messages", chrome: "page",
        blocks: [
          { t: "title", k: "help.messages.title" },
          { t: "lead", k: "help.messages.empty" },
          { t: "pill", k: "help.messages.sent" },
          { t: "btn", k: "help.messages.new", tone: "dark" },
        ],
      },
    ],
  },

  // ------------------------------------------------------------ Settings
  {
    id: "settings", label: "Settings", flow: "Settings", screen: "Settings",
    states: [
      {
        id: "account", label: "Account", chrome: "page",
        blocks: [
          { t: "title", k: "account.title" },
          { t: "section", k: "account.section.profile" },
          { t: "rows", items: [
            { label: "account.row.first" },
            { label: "account.row.last" },
            { label: "account.row.email" },
          ] },
          { t: "small", k: "account.profile.caption" },
          { t: "section", k: "account.section.reminders" },
          { t: "rows", items: [
            { label: "account.reminders.title", sub: "account.reminders.sub" },
            { label: "account.time.row", sub: "account.time.sub" },
          ] },
          { t: "small", k: "account.reminders.caption" },
          { t: "section", k: "account.section.learn" },
          { t: "rows", items: [{ label: "account.learn.row" }] },
          { t: "btn", k: "account.signout", tone: "ghost" },
          { t: "link", k: "account.delete" },
        ],
      },
      {
        id: "binder", label: "Binder timing", chrome: "page",
        blocks: [
          { t: "title", k: "account.title" },
          { t: "section", k: "account.section.binder" },
          { t: "small", k: "account.binder.caption" },
        ],
      },
      {
        id: "time", label: "Reminder time picker", chrome: "modal",
        blocks: [
          { t: "title", k: "account.time.title" },
          { t: "lead", k: "account.time.body" },
        ],
      },
      {
        id: "hold", label: "On hold", chrome: "page",
        blocks: [
          { t: "section", k: "settings.hold.label" },
          { t: "rows", items: [{ label: "settings.hold.sub" }] },
          { t: "btn", k: "settings.hold.cta" },
          { t: "small", k: "settings.hold.caption" },
        ],
      },
      {
        id: "delete", label: "Delete account", chrome: "modal",
        blocks: [
          { t: "title", k: "account.delete.title" },
          { t: "lead", k: "account.delete.body" },
          { t: "btn", k: "account.delete.confirm", tone: "red" },
          { t: "link", k: "account.delete.cancel" },
        ],
      },
    ],
  },

  // ------------------------------------------------------------ Messaging
  {
    id: "notifications", label: "Notifications", flow: "Messaging", screen: "Notification",
    note: "Push copy — rendered as a notification, not a screen.",
    states: [
      {
        id: "daily", label: "Daily reminder", chrome: "notification",
        blocks: [
          { t: "title", k: "notif.daily.title", small: true },
          { t: "lead", k: "notif.daily.body" },
          { t: "gap", h: 2 },
          { t: "btn", k: "notif.daily.action", tone: "ghost" },
        ],
      },
      {
        id: "start", label: "Start nudge", chrome: "notification",
        blocks: [
          { t: "title", k: "notif.start.title", small: true },
          { t: "lead", k: "notif.start.body" },
        ],
      },
    ],
  },

  // ------------------------------------------------------------ Everywhere
  {
    id: "tabbar", label: "Tab bar", flow: "Everywhere", screen: "Tab bar",
    states: [{
      id: "default", label: "Every screen", chrome: "today", tabHighlight: true,
      caption: "Four labels, on every screen in the app.",
      blocks: [
        { t: "lead", k: "today.dock.dayline" },
        { t: "btn", k: "today.cta.track" },
      ],
    }],
  },
  {
    id: "howto", label: "How-to sheet", flow: "Everywhere", screen: "How-to",
    states: [{
      id: "default", label: "Default", chrome: "modal",
      blocks: [{ t: "btn", k: "howto.close", tone: "dark" }],
    }],
  },
  {
    id: "emailteam", label: "Email to the team", flow: "Everywhere", screen: "Email to team",
    note: "The subject line on the email a customer's message arrives as.",
    states: [],
  },
];

/** Every copy key a list of blocks draws. */
export function keysInBlocks(blocks: Block[]): string[] {
  const out: string[] = [];
  for (const b of blocks) {
    const any = b as any;
    if (typeof any.k === "string") out.push(any.k);
    if (typeof any.meta === "string") out.push(any.meta);
    if (typeof any.sub === "string") out.push(any.sub);
    if (Array.isArray(any.ks)) out.push(...any.ks.filter((k: unknown) => typeof k === "string"));
    if (Array.isArray(any.items)) {
      for (const it of any.items) {
        for (const f of ["title", "label", "sub", "value"]) if (typeof it?.[f] === "string") out.push(it[f]);
      }
    }
  }
  return out;
}

/**
 * WHICH RAIL ENTRIES SHOW A KEY'S FIELD.
 *
 * 🔴 WHERE A KEY IS DRAWN BEATS WHAT IT IS CALLED. This used to be one lookup — the first
 * entry whose `screen` label equalled the copy row's `screen` AND whose `prefixes` matched
 * the key — and it broke the one promise of this screen, "click a line, land on its field":
 *
 *  - `finished.body` is labelled screen "Program complete" in the app's copy table, but the
 *    app's layout draws it on Today → "Program complete". No entry is labelled "Program
 *    complete", so the row went to a separate "Program complete · other copy" entry and the
 *    Today panel had no field for the very line highlighted on its phone.
 *  - First match wins, and Today's `nextup` entry has no prefixes (a catch-all) yet sits
 *    BEFORE `break`, `neworder` and `detoxnames` — so it swallowed every `break.*`,
 *    `confirm.*`, `order.*` and `detox.*` key, and those three entries had no rows at all
 *    and vanished from the rail.
 *
 * So, in order:
 *  1. every entry whose states DRAW the key (a key on two screens shows on both);
 *  2. plus the entry that claims it by the LONGEST matching prefix on its screen label;
 *  3. only if neither: the screen label's catch-all entry (no prefixes);
 *  4. otherwise: nothing — the caller parks it in "<screen> · other copy".
 * No naming convention is needed for a key the layout places.
 */
export function entriesForKey(
  screen: string,
  key: string,
  source: ScreenDef[],
  placed: Map<string, string[]>,
): string[] {
  const ids = [...(placed.get(key) ?? [])];
  let best: { id: string; len: number } | null = null;
  for (const s of source) {
    if (s.screen !== screen || !s.prefixes?.length) continue;
    for (const p of s.prefixes) {
      if (key.startsWith(p) && (!best || p.length > best.len)) best = { id: s.id, len: p.length };
    }
  }
  if (best && !ids.includes(best.id)) ids.push(best.id);
  if (!ids.length) {
    const catchAll = source.find((s) => s.screen === screen && !s.prefixes?.length);
    if (catchAll) ids.push(catchAll.id);
  }
  return ids;
}

/** key → ids of the entries whose states draw it, in rail order. */
export function placementIndex(source: ScreenDef[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const s of source) {
    for (const st of s.states) {
      for (const k of keysInBlocks(st.blocks || [])) {
        const list = m.get(k) || [];
        if (!list.includes(s.id)) list.push(s.id);
        m.set(k, list);
      }
    }
  }
  return m;
}
